import { ENTER, hasModifierKey, SPACE } from '@angular/cdk/keycodes';
import {
    ChangeDetectionStrategy,
    Component,
    Directive,
    ElementRef,
    EventEmitter,
    Output,
    ViewChild,
    ViewEncapsulation,
} from '@angular/core';
import { MatDialog } from '@angular/material/dialog';
import { UntilDestroy, untilDestroyed } from '@ngneat/until-destroy';
import Debug from 'debug';
import { BehaviorSubject } from 'rxjs';
import { AumicoEditableInformationPopupComponent } from '../../components/editable-information-popup/editable-information-popup.component';
import { MAT_DIALOG_CUSTOM_CLASS } from '../../constants/constants';
import { IPopupData } from '../../interfaces/popup-data.interface';

const debug = Debug('aumico:aumico-core:AumExpansionPanelInformationButton');
@UntilDestroy()
@Component({
    selector: 'aum-expansion-panel-information-i',
    styleUrls: ['./expansion-panel-information-i.scss'],
    templateUrl: './expansion-panel-information-i.html',
    encapsulation: ViewEncapsulation.None,
    changeDetection: ChangeDetectionStrategy.OnPush,
    host: {
        class: 'aum-expansion-panel-information',
        role: 'button',
        '(click)': '_toggle($event)',
        '(keydown)': '_keydown($event)',
    },
})
export class AumExpansionPanelInformationButton {
    informationText: string;
    showInformation: BehaviorSubject<boolean> = new BehaviorSubject<boolean>(false);
    @Output() emitInformationData = new EventEmitter<{ information: string; saveToRichText: boolean }>();
    @ViewChild('informationWrapper', { static: false }) set informationContent(informationContent: ElementRef) {
        if (informationContent != null) {
            this.informationText = informationContent.nativeElement.textContent;
            this.openInformationDialog();
        }
    }

    constructor(private dialog: MatDialog) {}

    _toggle(event: KeyboardEvent | Event) {
        debug('show information');
        this.showInformation.next(true);
        event.stopPropagation();
    }

    private openInformationDialog(): void {
        const popupData: IPopupData = {
            titleLocalizationKey: 'aumico_core_accordion_Information_dialog_title',
            isContentHtmlType: true,
            isContentEditable: true,
            contentMessage: this.informationText,
        };

        const dialogRef = this.dialog.open<
            AumicoEditableInformationPopupComponent,
            IPopupData,
            { information: string; saveToRichText: boolean }
        >(AumicoEditableInformationPopupComponent, {
            width: '600px',
            data: popupData,
            disableClose: true,
            panelClass: MAT_DIALOG_CUSTOM_CLASS,
        });

        dialogRef
            .afterClosed()
            .pipe(untilDestroyed(this))
            .subscribe((result) => {
                if (result != null) {
                    this.emitInformationData.emit(result);
                }
                this.showInformation.next(false);
            });
    }

    /** Handle keydown event calling to toggle() if appropriate. */
    _keydown(event: KeyboardEvent) {
        switch (event.keyCode) {
            // Toggle for space and enter keys.
            case SPACE:
            case ENTER:
                if (!hasModifierKey(event)) {
                    event.preventDefault();
                    this._toggle(event);
                }

                break;
            default:
                return;
        }
    }
}

/**
 * `<aum-panel-information>`
 *
 * This directive is to be used inside of the MatExpansionPanelHeader component.
 */
@Directive({
    selector: 'aum-panel-information',
    host: {
        class: 'aum-expansion-panel-header-information',
    },
})
export class AumExpansionPanelInformation {}
